/**
 * @module {Class} util/class
 * @parent utils.modules
 * 
 * Provides a class factory.
 *
 * @option {Class}
 * A simple class factory based on basic prototypal inheritance that one can
 * fall back on in situations where one cannot extend from a base class or use
 * a class factory from an application-level framework, e.g. , `can.Construct`.
 *
 * This is mostly the case when inheriting from prototypes that exist in other
 * third-party frameworks, such as Google Maps, which expect you to work with
 * the raw prototype chain.
 */
define('util/class',{
	/**
	 * @function create
	 *
	 * Creates a new class.
	 *
	 * @signature `Class.create(proto)`
	 * Creates a new class with a prototype built from the specified
	 * object.
	 * @param {{}} proto
	 * An object that defines the methods and properties that should go onto
	 * the prototype of the new class.
	 * @return {function(new:{})}
	 * The constructor function for the new class.
	 *
	 *
	 * @signature `Class.create(base, proto)`
	 * Creates a new class that inherits from a given base class and with
	 * a prototype built from the specified object.
	 * @param {function(new:{})} base The base class constructor function.
	 * @param {{}} proto
	 * An object that defines the methods and properties that should go onto
	 * the prototype of the new class.
	 * @return {function(new:)}
	 * The constructor function for the new class.
	 */
	create : function( base, proto ) {
		var Ctor, Glue;

		if ( proto == null ) { proto = base; base = Object; }

		Glue = function() {};
		Glue.prototype = base.prototype;

		Ctor = proto.hasOwnProperty( "constructor" )
			? proto.constructor
			: function() { return base.apply( this, arguments ); };

		Ctor.prototype = new Glue();
		Ctor.prototype.constructor = Ctor;

		for ( var name in proto ) {
			if ( name === "constructor" || !proto.hasOwnProperty( name )) continue;
			Ctor.prototype[ name ] = proto[ name ];
		}

		return Ctor;
	}
});
